import React from 'react';
import { Search, X, ChevronDown, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface FiltersProps {
  searchPlaceholder?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  filterGroups: {
    name: string;
    options: FilterOption[];
    selected: string[];
    onToggle: (value: string) => void;
  }[];
  onClearAll: () => void;
  nearMeFilter?: {
    enabled: boolean;
    onToggle: () => void;
    available: boolean;
  };
}

export function Filters({
  searchPlaceholder = 'Search...',
  searchValue,
  onSearchChange,
  filterGroups,
  onClearAll,
  nearMeFilter,
}: FiltersProps) {
  const { t } = useTranslation();
  const hasActiveFilters = filterGroups.some(group => group.selected.length > 0) || searchValue || nearMeFilter?.enabled;
  const [expandedGroups, setExpandedGroups] = React.useState<Record<string, boolean>>({});

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(e.target.value);
  };

  return (
    <div className="mb-12 bg-gray-custom/10 backdrop-blur-sm rounded-xl p-6">
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-light/40" />
          <input
            type="text"
            value={searchValue}
            onChange={handleSearchChange}
            placeholder={searchPlaceholder}
            className="w-full pl-10 pr-4 py-2 bg-[#232323] border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:outline-none focus:border-gray-custom/40 transition-colors"
          />
        </div>
        {nearMeFilter && (
          <button
            onClick={nearMeFilter.onToggle}
            disabled={!nearMeFilter.available}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              nearMeFilter.enabled
                ? 'bg-primary text-light'
                : nearMeFilter.available
                ? 'bg-[#232323] text-light/60 hover:bg-[#2a2a2a]'
                : 'bg-[#232323] text-light/40 cursor-not-allowed'
            }`}
          >
            <MapPin className="w-5 h-5" />
            <span>{t('musicians.filters.closeToMe')}</span>
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filterGroups.map((group) => {
          const isExpanded = expandedGroups[group.name] ?? false;
          const selectedCount = group.selected.length;

          return (
            <div key={group.name} className="space-y-3">
              <button
                onClick={() => toggleGroup(group.name)}
                className="w-full flex items-center justify-between text-left p-2 rounded-lg hover:bg-[#232323] transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <h3 className="text-sm font-medium text-light/80">{group.name}</h3>
                  {selectedCount > 0 && (
                    <span className="px-2 py-0.5 text-xs bg-primary text-light rounded-full">
                      {selectedCount}
                    </span>
                  )}
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-light/40 transition-transform ${
                    isExpanded ? 'transform rotate-180' : ''
                  }`}
                />
              </button>

              <div
                className={`grid grid-cols-2 gap-2 transition-all duration-200 ${
                  isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                }`}
              >
                {group.options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => group.onToggle(option.value)}
                    className={`px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-all ${
                      group.selected.includes(option.value)
                        ? 'bg-primary text-light hover:bg-primary/80'
                        : 'bg-[#232323] text-light/60 hover:bg-[#2a2a2a]'
                    }`}
                  >
                    <span className="truncate">{option.label}</span>
                    {option.count !== undefined && (
                      <span className={`text-xs ml-2 ${
                        group.selected.includes(option.value)
                          ? 'text-light'
                          : 'text-light/40'
                      }`}>
                        {option.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {hasActiveFilters && (
        <div className="mt-6 pt-6 border-t border-gray-custom/10">
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {filterGroups.map(group =>
                group.selected.map(value => {
                  const option = group.options.find(opt => opt.value === value);
                  if (!option) return null;

                  return (
                    <button
                      key={`${group.name}-${value}`}
                      onClick={() => group.onToggle(value)}
                      className="px-3 py-1.5 rounded-full text-sm bg-primary text-light flex items-center space-x-2 hover:bg-primary/80 transition-colors"
                    >
                      <span>{option.label}</span>
                      <X className="w-3 h-3" />
                    </button>
                  );
                })
              )}
              {searchValue && (
                <button
                  onClick={() => onSearchChange('')}
                  className="px-3 py-1.5 rounded-full text-sm bg-primary text-light flex items-center space-x-2 hover:bg-primary/80 transition-colors"
                >
                  <span>"{searchValue}"</span>
                  <X className="w-3 h-3" />
                </button>
              )}
              {nearMeFilter?.enabled && (
                <button
                  onClick={nearMeFilter.onToggle}
                  className="px-3 py-1.5 rounded-full text-sm bg-primary text-light flex items-center space-x-2 hover:bg-primary/80 transition-colors"
                >
                  <MapPin className="w-3 h-3" />
                  <span>{t('musicians.filters.closeToMe')}</span>
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <button
              onClick={onClearAll}
              className="ml-4 px-4 py-2 text-sm text-light/60 hover:text-light flex items-center space-x-2 bg-[#232323] rounded-lg hover:bg-[#2a2a2a] transition-colors"
            >
              <X className="w-4 h-4" />
              <span>{t('common.clearAll')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}